import { useRef } from "react";
import { Popover } from "@datapay-by-datacom/design-system";
import type { SortByField, SortDirection } from "../../types";
import { PillButton } from "../../../shared/components/DatePill/PillButton";
import styles from "./ControlBar.module.css";

interface SortOption {
  label: string;
  sortBy: SortByField;
  sortDirection: SortDirection;
}

const SORT_OPTIONS: SortOption[] = [
  { label: "Newest first", sortBy: "completedAt", sortDirection: "desc" },
  { label: "Oldest first", sortBy: "completedAt", sortDirection: "asc" },
  {
    label: "Match Rate: High to Low",
    sortBy: "matchRate",
    sortDirection: "desc",
  },
  {
    label: "Match Rate: Low to High",
    sortBy: "matchRate",
    sortDirection: "asc",
  },
  {
    label: "Duration: Longest first",
    sortBy: "durationInSeconds",
    sortDirection: "desc",
  },
  {
    label: "Duration: Shortest first",
    sortBy: "durationInSeconds",
    sortDirection: "asc",
  },
];

function sortKey(
  sortBy: SortByField,
  sortDirection: SortDirection,
): string {
  return `${sortBy}:${sortDirection}`;
}

export interface SortPillProps {
  sortBy: SortByField;
  sortDirection: SortDirection;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (sortBy: SortByField, sortDirection: SortDirection) => void;
}

export function SortPill({
  sortBy,
  sortDirection,
  isOpen,
  onOpenChange,
  onChange,
}: SortPillProps) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const currentKey = sortKey(sortBy, sortDirection);
  const currentLabel =
    SORT_OPTIONS.find((o) => sortKey(o.sortBy, o.sortDirection) === currentKey)
      ?.label ?? "Newest first";
  const isActive =
    sortBy !== "completedAt" || sortDirection !== "desc";

  function handleSelect(opt: SortOption) {
    onChange(opt.sortBy, opt.sortDirection);
    onOpenChange(false);
  }

  return (
    <>
      <PillButton
        label="Sort"
        value={currentLabel}
        isActive={isActive}
        isOpen={isOpen}
        triggerRef={triggerRef}
        onClick={() => onOpenChange(!isOpen)}
        testId="sort-pill"
      />
      <Popover
        triggerRef={triggerRef}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        placement="bottom"
      >
        <ul className={styles.sortList} role="listbox" data-testid="sort-options">
          {SORT_OPTIONS.map((opt) => {
            const key = sortKey(opt.sortBy, opt.sortDirection);
            const selected = key === currentKey;
            return (
              <li
                key={key}
                role="option"
                aria-selected={selected}
                className={`${styles.sortOption} ${selected ? styles.sortOptionSelected : ""}`}
                onClick={() => handleSelect(opt)}
                data-testid={`sort-option-${key}`}
              >
                {selected && (
                  <span className={styles.checkmark} aria-hidden="true">
                    &#10003;
                  </span>
                )}
                {opt.label}
              </li>
            );
          })}
        </ul>
      </Popover>
    </>
  );
}
